/**
 * API-Schlüssel anlegen, ansehen, widerrufen.
 *
 * Läuft ohne Edge Function direkt gegen `public.api_keys` unter RLS. Das ist
 * kein Abkürzen: die Policy lässt nur die eigenen Zeilen durch, und der
 * Trigger `api_keys_guard` macht die Regeln hart — beim Anlegen sind nur die
 * Berechtigungen `{simulate}` oder `{read,write}` erlaubt, und beim Ändern ist
 * alles unveränderlich außer `revoked_at`. Ein Schlüssel lässt sich also weder
 * nachträglich aufwerten noch wiederbeleben, egal was der Browser schickt.
 *
 * DER KLARTEXT ERSCHEINT GENAU EINMAL. Gespeichert wird nur sein SHA-256; es
 * gibt kein Nachschlagen, auch nicht für uns. Die Oberfläche sagt das an der
 * Stelle, an der es zählt, und nicht im Kleingedruckten.
 *
 * WARUM `simulate` UND NICHT `{read,write}`: Die MCP-Function verlangt hart
 * die Berechtigung `simulate`. Ein Schlüssel mit `{read,write}` kommt dort
 * nicht durch — das ist Absicht und wäre hier eine stille Falle.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ApiFehler, ladeStatus } from '../api';
import { claudeCodeBefehl, schluesselAnzeige } from '../schluessel';
import { legeSchluesselAn } from '../schluessel-anlegen';
import { useSitzung } from '../session';
import { supabase } from '../supabase';
import { Abschnitt, AppRahmen, Feld, Hinweis, KleinerKnopf, Knopf, Kopierzeile, Panel, Platzhalter, Seitenkopf, Verweis, Zeile } from '../ui';
import { locale, t } from '../sprache';

interface SchluesselZeile {
  key_hash: string;
  key_prefix: string;
  label: string | null;
  created_at: string;
  last_used: string | null;
  revoked_at: string | null;
}

const zeitpunkt = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export default function Schluessel() {
  const { session, abmelden } = useSitzung();
  const [kontoId, setKontoId] = useState<string>('');
  const [liste, setListe] = useState<SchluesselZeile[]>([]);
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(true);
  const [bezeichnung, setBezeichnung] = useState('');
  const [legtAn, setLegtAn] = useState(false);
  const [frisch, setFrisch] = useState<string>('');

  const holen = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      // billing/status zuerst: es liefert die Konto-id UND legt die Zeile in
      // public.accounts an, falls ein frisch bestaetigtes Konto sie noch nicht
      // hat. Ohne sie scheitert jedes Einfuegen am Fremdschluessel.
      const status = await ladeStatus();
      setKontoId(status.account_id);
      const { data, error } = await supabase
        .from('api_keys')
        .select('key_hash, key_prefix, label, created_at, last_used, revoked_at')
        .order('created_at', { ascending: false });
      if (error) throw new ApiFehler(t(`Die Schlüssel ließen sich nicht laden: ${error.message}`, `The keys could not be loaded: ${error.message}`));
      setListe((data ?? []) as SchluesselZeile[]);
    } catch (e) {
      const f = e as ApiFehler;
      setFehler(f.message);
      if (f.abgemeldet) void abmelden();
    } finally {
      setLaedt(false);
    }
  }, [abmelden]);

  useEffect(() => { void holen(); }, [holen]);

  async function anlegen(e: FormEvent) {
    e.preventDefault();
    if (!kontoId) return;
    setLegtAn(true);
    setFehler('');
    setFrisch('');
    let key: string;
    try {
      key = await legeSchluesselAn(kontoId, bezeichnung);
    } catch (e) {
      setFehler((e as Error).message);
      return;
    } finally {
      setLegtAn(false);
    }
    setFrisch(key);
    setBezeichnung('');
    await holen();
  }

  async function widerrufen(zeile: SchluesselZeile) {
    const sicher = window.confirm(t(
      `Schlüssel ${zeile.key_prefix}… widerrufen?\n\n`
      + 'Das lässt sich nicht rückgängig machen. Alles, was diesen Schlüssel benutzt, verliert sofort den Zugang.',
      `Revoke key ${zeile.key_prefix}…?\n\n`
      + 'This cannot be undone. Anything that uses this key loses access immediately.',
    ));
    if (!sicher) return;
    setFehler('');
    const { error } = await supabase
      .from('api_keys')
      .update({ revoked_at: new Date().toISOString() })
      .eq('key_hash', zeile.key_hash);
    if (error) {
      setFehler(t(`Der Widerruf ist nicht durchgegangen: ${error.message}`, `The revocation did not go through: ${error.message}`));
      return;
    }
    await holen();
  }

  const aktiv = liste.filter(k => !k.revoked_at);
  const widerrufen_ = liste.filter(k => k.revoked_at);

  return (
    <AppRahmen email={session?.user?.email ?? ''} abmelden={() => void abmelden()}>
      <Link to="/account" className="mb-3 inline-block text-[0.875rem] text-text-hell no-underline hover:text-marke-tief">{t('← Konto', '← Account')}</Link>
      <Seitenkopf
        titel={t('API-Schlüssel', 'API keys')}
        unter={t(<>Für Stellen ohne Anmeldung im Browser: ein Server, ein Build-Schritt, ein Skript.
          Für claude.ai und Claude Code reicht meist der Connector, der dich im Browser anmeldet (<Verweis to="/account#verbinden">Mit Claude verbinden</Verweis>).</>,
        <>For places without a browser sign-in: a server, a build step, a script.
          For claude.ai and Claude Code, the connector that signs you in through the browser is usually enough (<Verweis to="/account#verbinden">Connect to Claude</Verweis>).</>)}
      />

      {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}

      {frisch && (
        <section className="mb-8 rounded-karte border border-marke/40 bg-marke/[0.05] p-4 sm:p-6">
          <h2 className="m-0 mb-1.5 text-base font-semibold text-tinte">{t('Dein neuer Schlüssel', 'Your new key')}</h2>
          <p className="mt-0 mb-4 max-w-[62ch] text-[0.9375rem] leading-relaxed text-text-leise">
            {t(<>
              Kopier ihn jetzt. Wir speichern nur seine Prüfsumme. Wenn du diese Seite verlässt,
              ist der Klartext weg, auch für uns.
            </>, <>
              Copy it now. We only store its checksum. When you leave this page,
              the plain text is gone, also for us.
            </>)}
          </p>
          <Kopierzeile text={frisch} />
          <p className="mt-4 mb-2 text-[0.9375rem] leading-relaxed text-text-leise">{t('Oder gleich als Befehl für Claude Code:', 'Or right away as a command for Claude Code:')}</p>
          <Kopierzeile text={claudeCodeBefehl(frisch)} />
          <div className="mt-4 flex flex-wrap gap-2">
            <KleinerKnopf type="button" onClick={() => setFrisch('')}>{t('Ich habe ihn gesichert', 'I have saved it')}</KleinerKnopf>
          </div>
        </section>
      )}

      <Abschnitt titel={t('Neuen Schlüssel anlegen', 'Create a new key')}>
        <Panel className="px-4 pt-4 pb-1 sm:px-5 sm:pt-5">
          {/* Feld und Knopf nebeneinander ab sm: ein Formular mit einem Feld
              braucht keinen Knopf ueber die volle Breite. */}
          <form onSubmit={anlegen} className="grid gap-x-3 sm:grid-cols-[1fr_auto] sm:items-start">
            <Feld
              label={t('Bezeichnung', 'Label')}
              hinweis={t('Wofür ist er? Zum Beispiel „Claude Code auf dem Laptop“.', 'What is it for? For example “Claude Code on the laptop”.')}
              value={bezeichnung}
              onChange={e => setBezeichnung(e.target.value)}
              disabled={legtAn || !kontoId}
              maxLength={60}
            />
            <div className="mb-4 sm:mt-[1.625rem] sm:mb-0">
              <Knopf type="submit" laedt={legtAn} disabled={!kontoId}>{t('Schlüssel anlegen', 'Create key')}</Knopf>
            </div>
          </form>
        </Panel>
      </Abschnitt>

      <Abschnitt titel={t('Aktiv', 'Active')} zahl={aktiv.length}>
        {laedt && !liste.length && (
          <Panel>{[0, 1].map(i => <Zeile key={i}><Platzhalter zeilen={2} /></Zeile>)}</Panel>
        )}
        {!laedt && !aktiv.length && (
          <Panel className="px-4 py-5 sm:px-5">
            <p className="m-0 text-[0.9375rem] leading-relaxed text-text-leise">
              {t('Noch keiner. Mit einem Schlüssel verbindest du 99takes direkt aus Claude Code heraus.', 'None yet. With a key, you connect 99takes directly from Claude Code.')}
            </p>
          </Panel>
        )}
        {aktiv.length > 0 && (
          <Panel>
            {aktiv.map(k => (
              <div key={k.key_hash} className="flex flex-wrap items-center gap-x-4 gap-y-2.5 border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:flex-nowrap sm:px-5">
                <div className="min-w-0 flex-[1_1_12rem]">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="text-[0.9375rem] font-semibold [overflow-wrap:anywhere] text-tinte">{k.label}</span>
                    <code className="font-mono text-[0.8125rem] text-text-leise">{schluesselAnzeige(k.key_prefix)}</code>
                  </div>
                  <div className="mt-0.5 text-[0.8125rem] text-text-hell tabular-nums">
                    {t('angelegt', 'created')} {zeitpunkt(k.created_at)} · {k.last_used
                      ? t(`zuletzt benutzt ${zeitpunkt(k.last_used)}`, `last used ${zeitpunkt(k.last_used)}`)
                      : t('noch nicht benutzt', 'not used yet')}
                  </div>
                </div>
                <KleinerKnopf
                  type="button" onClick={() => void widerrufen(k)}
                  className="shrink-0 hover:!border-schlecht hover:!text-schlecht"
                >
                  {t('Widerrufen', 'Revoke')}
                </KleinerKnopf>
              </div>
            ))}
          </Panel>
        )}

        {/* Widerrufene bleiben sichtbar, aber eingeklappt: sie sind Protokoll, keine Aufgabe. */}
        {widerrufen_.length > 0 && (
          <details className="group mt-3 [&_summary::-webkit-details-marker]:hidden">
            <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-[0.875rem] text-text-leise hover:text-text">
              <span aria-hidden className="inline-block text-text-hell transition-transform duration-200 group-open:rotate-90">▸</span>
              {widerrufen_.length === 1
                ? t('1 widerrufener Schlüssel', '1 revoked key')
                : t(`${widerrufen_.length} widerrufene Schlüssel`, `${widerrufen_.length} revoked keys`)}
            </summary>
            <Panel className="mt-2.5">
              {widerrufen_.map(k => (
                <div key={k.key_hash} className="border-t border-linie-zart px-4 py-3 text-text-hell first:border-t-0 sm:px-5">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="text-[0.9375rem] [overflow-wrap:anywhere]">{k.label}</span>
                    <code className="font-mono text-[0.8125rem] line-through">{schluesselAnzeige(k.key_prefix)}</code>
                  </div>
                  <div className="mt-0.5 text-[0.8125rem] tabular-nums">{t('widerrufen', 'revoked')} {zeitpunkt(k.revoked_at)}</div>
                </div>
              ))}
            </Panel>
          </details>
        )}
      </Abschnitt>

      <Abschnitt titel={t('So benutzt du ihn', 'How to use it')}>
        <p className="mt-0 mb-3 max-w-[62ch] text-[0.9375rem] leading-relaxed text-text-leise">
          {t('Einmal in Claude Code eintragen:', 'Add it once in Claude Code:')}
        </p>
        <Kopierzeile text={claudeCodeBefehl('ntk_…')} />
        <p className="mt-2 mb-0 text-[0.8125rem] text-text-hell">
          {t(<><code className="font-mono">ntk_…</code> durch deinen Schlüssel ersetzen.</>, <>Replace <code className="font-mono">ntk_…</code> with your key.</>)}
        </p>
        <p className="mt-4 mb-2 text-[0.9375rem] leading-relaxed text-text-leise">
          {t('Danach fragst du dort ganz normal, zum Beispiel so:', 'Then you just ask there as usual, for example:')}
        </p>
        <ol className="mt-0 mb-0 max-w-[68ch] space-y-2 pl-5 text-[0.9375rem] leading-relaxed text-text">
          {t(<>
            <li>„Welche Zielgruppen habe ich?“ Passt keine, sag „Lass uns Personas für mein Angebot bauen“. 99takes liest deine Wettbewerber, sucht Kundenstimmen und entwirft die Personas. Das Speichern kostet einmalig 15 Credits.</li>
            <li>„Was kostet ein Run gegen meine Zielgruppe?“ Nennt Personas, Jobs und den Preis, <em>bevor</em> etwas abgebucht wird.</li>
            <li>Dann der Run selbst, zum angesagten Preis.</li>
          </>, <>
            <li>“Which audiences do I have?” If none fits, say “Let's build personas for my offer”. 99takes reads your competitors, looks for customer voices and drafts the personas. Saving costs 15 credits once.</li>
            <li>“What does a run against my audience cost?” Names personas, jobs and the price <em>before</em> anything is charged.</li>
            <li>Then the run itself, at the stated price.</li>
          </>)}
        </ol>
      </Abschnitt>

      {/*
        Hier stand bis zum 25.09.2026 „Er darf Simulationen fahren und sonst
        nichts“. Das war falsch: die Auth prueft `simulate` genau einmal, danach
        steht der gesamte MCP offen — eine Pruefung je Werkzeug gibt es nicht.
        Eine zu enge Rechteangabe ist hier schlimmer als gar keine, weil an
        dieser Stelle jemand entscheidet, ob er den Schluessel weitergibt.
      */}
      <Abschnitt titel={t('Was ein Schlüssel darf', 'What a key can do')}>
        <p className="mt-0 mb-2 max-w-[68ch] text-[0.9375rem] leading-relaxed text-text">
          {t(<>
            Er öffnet den gesamten Zugang für <strong>dein</strong> Konto: Simulationen
            fahren, Zielgruppen anlegen und ändern, Ausbauten führen, Guthaben lesen
            und Berichte veröffentlichen, die danach über einen Link erreichbar sind.
          </>, <>
            It opens full access to <strong>your</strong> account: run simulations,
            create and change audiences, run page builds, read the balance
            and publish reports, which are then reachable through a link.
          </>)}
        </p>
        <p className="mt-0 mb-4 max-w-[68ch] text-[0.9375rem] leading-relaxed text-text-leise">
          {t(<>
            Was er nicht kann: dein Konto ändern, weitere Schlüssel anlegen oder
            widerrufen, und an ein fremdes Konto kommt er nicht.
          </>, <>
            What it cannot do: change your account, create or revoke other keys,
            and it cannot reach anyone else's account.
          </>)}
        </p>
        <Hinweis>
          {t(<>
            Wer den Schlüssel hat, kann all das. Leg für jede Maschine einen eigenen an,
            dann kannst du einen einzelnen widerrufen, ohne die anderen zu treffen.
          </>, <>
            Whoever has the key can do all of this. Create a separate one for each machine,
            then you can revoke one without affecting the others.
          </>)}
        </Hinweis>
      </Abschnitt>
    </AppRahmen>
  );
}
